import { type Ref } from "react";
import { Eye } from "lucide-react";
import { useDraggable } from "@dnd-kit/core";
import { useConfiguratorContext } from "../context/DashboardConfiguratorContext";
import { TILE_COLORS } from "../constants";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import type { TileConfig } from "../types";
import type { WidgetId } from "@/other/constants/EntityManagerConfig";
import { cn } from "@/modern/utils";

interface HiddenTilesSidebarProps {
  /** Attached to the sidebar root so ConfiguratorGrid can hit-test drops. */
  sidebarRef: Ref<HTMLDivElement>;
  /** True while a visible grid tile is being RGL-dragged. Shows drop hint. */
  isDropTarget: boolean;
}

export function HiddenTilesSidebar({
  sidebarRef,
  isDropTarget,
}: HiddenTilesSidebarProps) {
  const { hiddenTiles, handleToggleVisibility } = useConfiguratorContext();

  return (
    <div
      ref={sidebarRef}
      className={cn(
        "w-[180px] border-l border-border bg-background/50 flex flex-col flex-shrink-0 transition-colors sticky top-0",
        isDropTarget && "ring-2 ring-inset ring-primary/50 bg-primary/5"
      )}
    >
      <div className="px-3 pt-3 pb-2">
        <label className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
          Hidden
        </label>
      </div>
      <ScrollArea className="flex-1">
        <div className="px-3 pb-3 flex flex-col gap-2">
          {hiddenTiles.length === 0 && !isDropTarget && (
            <p className="text-[11px] text-muted-foreground/60 py-4 text-center">
              No hidden tiles
            </p>
          )}
          {hiddenTiles.map((tile) => (
            <DraggableHiddenTile
              key={tile.id}
              tile={tile}
              onToggleVisibility={handleToggleVisibility}
            />
          ))}
          {isDropTarget && (
            <p className="text-[11px] text-primary/70 py-4 text-center font-medium">
              Drop to hide
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

interface DraggableHiddenTileProps {
  tile: TileConfig;
  onToggleVisibility: (id: WidgetId) => void;
}

export function DraggableHiddenTile({
  tile,
  onToggleVisibility,
}: DraggableHiddenTileProps) {
  const bgColor = TILE_COLORS[tile.color] ?? TILE_COLORS.blue;

  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({
    id: tile.id,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(
        "relative group rounded-md overflow-hidden cursor-grab select-none",
        isDragging && "opacity-30 cursor-grabbing"
      )}
      style={{ minHeight: 60 }}
    >
      <div
        className="absolute inset-0 rounded-md pointer-events-none"
        style={{ backgroundColor: bgColor, opacity: 0.72 }}
      />
      <div className="relative h-full min-h-[60px] flex items-center justify-center px-3">
        <span className="text-[11px] font-medium text-white/90 truncate">
          {tile.title}
        </span>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleVisibility(tile.id);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-80 transition-opacity p-1.5 rounded hover:bg-black/20"
        title="Show tile"
      >
        <Eye className="w-4 h-4 text-white/70" />
      </button>
    </div>
  );
}
